import type { ReactNode } from "react";
import { Avatar, Box, Button, Chip, Stack, Typography } from "@mui/material";
import { playerHandleSuffix } from "@bw/shared/components";
import type { UserProfileResponse } from "@bw/shared/types/generated";
import { Link as RouterLink } from "react-router-dom";
import { hasSafeAvatar, initials } from "../../../common/avatar";

function visibilityLabel(visibility: string | null | undefined): string | null {
  if (visibility === "public") return "Public";
  if (visibility === "private") return "Private";
  return visibility ?? null;
}

/**
 * The own-profile header: avatar, name + #id, one facts line (visibility and,
 * when the app supplies it, the competitive standing), then edit and view.
 */
export function ProfileSummary({
  profile,
  standing,
}: {
  profile: UserProfileResponse;
  standing?: ReactNode;
}) {
  const visibility = visibilityLabel(profile.visibility);
  return (
    <Stack component="section" aria-label="Your profile" spacing={1.5}>
      <Stack direction="row" spacing={2} sx={{ alignItems: "center", minWidth: 0 }}>
        <Avatar
          src={hasSafeAvatar(profile.avatar) ? (profile.avatar ?? undefined) : undefined}
          alt=""
          sx={{ width: 56, height: 56, fontSize: 20, fontWeight: 700 }}
        >
          {initials(profile.display_name)}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: "1.125rem", fontWeight: 700, overflowWrap: "anywhere" }}>
            {profile.display_name}{" "}
            <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              {playerHandleSuffix(profile.user_id)}
            </Typography>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {visibility}
            {visibility ? standing : null}
          </Typography>
          {profile.account_status !== "active" ? (
            <Chip
              label={profile.account_status.replaceAll("_", " ")}
              size="small"
              color="warning"
              variant="outlined"
              sx={{ mt: 0.5, textTransform: "capitalize" }}
            />
          ) : null}
        </Box>
      </Stack>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
        <Button component={RouterLink} to="/me/edit" variant="outlined" color="inherit" sx={{ borderRadius: 999 }}>
          Edit profile
        </Button>
        <Button
          component={RouterLink}
          to={"/players/" + encodeURIComponent(profile.user_id)}
          variant="outlined"
          color="inherit"
          sx={{ borderRadius: 999 }}
        >
          View public profile
        </Button>
      </Box>
    </Stack>
  );
}
